/* ============================================================
   味覚編集。 shared interactions (all pages)
   ============================================================ */

// ---------- screenshot helper (dev only): #shot=<y> shifts page instantly ----------
if (location.hash.startsWith('#shot=')) {
  const v = location.hash.slice(6);
  document.documentElement.classList.add('is-shot');
  if (v === 'menu') {
    window.addEventListener('load', () => document.getElementById('menuBtn').click());
  } else {
    document.body.style.transform = `translateY(${-(parseInt(v, 10) || 0)}px)`;
  }
}

const $ = (sel, el = document) => el.querySelector(sel);
const $$ = (sel, el = document) => [...el.querySelectorAll(sel)];

// ---------- 長体の字幅詰め ----------
// .ja-c は scale で字面だけを縮めるので、レイアウト上の箱は元の幅のまま残る。
// 幅が中身で決まるタグチップでは箱が縮まず「長体が掛かっていない」大きさに
// 見えるため、縮んだぶん（両側 (1 - 0.9) / 2）を負のマージンで詰める。
// margin は自身の幅に影響しないので ResizeObserver がループすることはない。
const JA_COND_FIT_SEL = '.tags .ja-c, .qv-style-chip .ja-c';
const jaCond = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ja-cond')) || 0.9;
const fitJaCond = el => {
  el.style.marginInline = `${-el.offsetWidth * (1 - jaCond) / 2}px`;
};
const jaCondObserver = window.ResizeObserver
  ? new ResizeObserver(entries => entries.forEach(entry => fitJaCond(entry.target)))
  : null;
$$(JA_COND_FIT_SEL).forEach(el => {
  fitJaCond(el);
  if (jaCondObserver) jaCondObserver.observe(el);
});

// ---------- Shopify bridge ----------
// theme.liquid publishes the CDN asset base, the localised routes and the few
// strings this file writes into the DOM. The fallbacks keep the static comp
// working when the same file is opened outside a Shopify context.
const THEME = window.MIKAKU || {};
const ROUTES = THEME.routes || {};
const asset = file => (THEME.assetBase || 'assets/img/') + file;
const str = (key, fallback) => (THEME.strings && THEME.strings[key]) || fallback;

// ---------- proportional scaling: the comps are fixed-width boards (1200 on PC,
// 385 on SP), so the page is zoomed to whatever the device actually is. Without
// this the SP layout keeps its 385px measurements and leaves a growing strip of
// dead space down the right of every phone wider than the comp.
const SP_DESIGN_W = 385;
const PC_DESIGN_W = 1200;
// snippets/page-zoom.liquid defines this and runs it inline, before the first
// paint — this file is deferred, so applying the zoom here alone showed the
// unscaled board for a couple of frames on load. The local copy is the
// fallback for the static build, which has no Liquid layout.
// Scaling runs in both directions. Only scaling up left 768-1200px unscaled,
// where the 1200px board is wider than the viewport: every page picked up a
// horizontal scrollbar and the menu panel, being the rightmost element, ran
// off screen.
const applyPageZoom = window.mikakuPageZoom || function () {
  const w = document.documentElement.clientWidth;
  document.body.style.zoom = String(w / (w <= 767 ? SP_DESIGN_W : PC_DESIGN_W));
};
// The PC menu positions both columns absolutely against the comp's 800px board.
// When the viewport is wide but short the board scales up by width, so less than
// 800px of height is left and the section links run into the menu footer. Shrink
// the gap to whatever the remaining height allows, never above the comp's value.
const MENU_GAP_MAX = 36.6;
function fitMenuColumns() {
  const col = document.querySelector('.menu-col-sub');
  if (!col || window.innerWidth < 768) return;
  const items = [...col.children];
  if (items.length < 2) return;
  col.style.rowGap = '';
  const content = items.reduce((sum, el) => sum + el.offsetHeight, 0);
  const gap = Math.min(MENU_GAP_MAX, Math.max(0, (col.clientHeight - content) / (items.length - 1)));
  col.style.rowGap = gap + 'px';
}

function applyLayoutMetrics() {
  if (THEME.enablePageZoom !== false) applyPageZoom();
  fitMenuColumns();
}

applyLayoutMetrics();
window.addEventListener('resize', applyLayoutMetrics);

// ---------- header: solid after hero (TOP) / always dark on subpages ----------
const header = $('#siteHeader');
const hero = $('#hero');
if (header && hero) {
  new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-solid', !entry.isIntersecting),
    { rootMargin: '-80px 0px 0px 0px' }
  ).observe(hero);
} else if (header) {
  window.addEventListener('scroll', () => {
    header.classList.toggle('is-solid', window.scrollY > 160);
  }, { passive: true });
}

// 全ページ共通: 下スクロールでヘッダーを隠し、上スクロールで出現させる。
if (header) {
  let lastY = window.scrollY;
  let ticking = false;
  const updateDetailHeader = () => {
    const y = window.scrollY;
    const delta = y - lastY;
    if (y <= 16 || delta < -3) {
      header.classList.remove('is-hidden');
    } else if (y > 140 && delta > 3 && !header.classList.contains('is-menu-open')) {
      header.classList.add('is-hidden');
    }
    lastY = y;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateDetailHeader);
    }
  }, { passive: true });
}

// ---------- fullscreen menu ----------
const menuBtn = $('#menuBtn');
const menuOverlay = $('#menuOverlay');
if (menuBtn && menuOverlay) {
  const setMenu = open => {
    menuOverlay.classList.toggle('is-open', open);
    menuBtn.classList.toggle('is-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    header.classList.toggle('is-menu-open', open);
    if (open) menuOverlay.dataset.view = 'top';
  };
  menuBtn.addEventListener('click', () => setMenu(!menuOverlay.classList.contains('is-open')));
  const menuClose = $('#menuClose');
  if (menuClose) menuClose.addEventListener('click', () => setMenu(false));
  // パネルの外（左の透け帯）をクリックしたら閉じる。オーバーレイ自体は
  // visibility:hidden（パネルだけ visible）でクリックを受けないため、
  // document 側で捕まえる
  document.addEventListener('click', e => {
    if (!menuOverlay.classList.contains('is-open')) return;
    if (e.target.closest('.menu-panel') || e.target.closest('#menuBtn')) return;
    setMenu(false);
  });
  $$('a[href]', menuOverlay).forEach(a => {
    a.addEventListener('click', () => setMenu(false));
  });
  // AI menu_1-4: hovering the main links swaps the panel view. Pointer-driven,
  // so it stays on hover-capable devices — touch uses the drag below instead and
  // the two would otherwise fight over dataset.view.
  if (window.matchMedia('(hover: hover)').matches) {
    $$('.menu-link[data-view]', menuOverlay).forEach(link => {
      link.addEventListener('mouseenter', () => { menuOverlay.dataset.view = link.dataset.view; });
    });
    const mainCol = $('.menu-col-main', menuOverlay);
    if (mainCol) mainCol.addEventListener('mouseleave', () => { menuOverlay.dataset.view = 'top'; });
  }

  // SP has no hover, so a vertical drag over the board steps through the same
  // four views (SP_menu_1-4). The board already fits one screen, so nothing is
  // meant to scroll: the gesture is consumed here, which is also what keeps the
  // page underneath from moving. A plain tap never reaches this (no touchmove),
  // so the links still navigate.
  if (window.matchMedia('(max-width: 767px)').matches) {
    const VIEWS = ['top', 'select', 'about', 'contact'];
    const STEP = 40;                    // drag distance, in px, per view change
    let travel = 0, lastY = 0;
    const stepView = dir => {
      const i = Math.max(0, VIEWS.indexOf(menuOverlay.dataset.view || 'top'));
      const next = Math.min(VIEWS.length - 1, Math.max(0, i + dir));
      if (next !== i) menuOverlay.dataset.view = VIEWS[next];
    };
    const consume = delta => {
      // reversing direction starts a fresh count, otherwise leftover travel from
      // the previous direction has to be paid off before the view turns back
      if (travel && Math.sign(delta) !== Math.sign(travel)) travel = 0;
      travel += delta;
      if (Math.abs(travel) >= STEP) {
        stepView(Math.sign(travel));
        travel = 0;
      }
    };
    const isOpen = () => menuOverlay.classList.contains('is-open');
    menuOverlay.addEventListener('wheel', e => {
      if (!isOpen()) return;
      e.preventDefault();
      consume(e.deltaY);
    }, { passive: false });
    menuOverlay.addEventListener('touchstart', e => {
      lastY = e.touches[0].clientY;
      travel = 0;
    }, { passive: true });
    menuOverlay.addEventListener('touchmove', e => {
      if (!isOpen()) return;
      e.preventDefault();
      const y = e.touches[0].clientY;
      consume(lastY - y);
      lastY = y;
    }, { passive: false });
  }
}

// ---------- generic fade slider (hero / break) ----------
function fadeSlider(slideSel, pagerEl, interval = 6000) {
  const slides = $$(slideSel);
  if (!slides.length) return;
  const dots = pagerEl ? $$('li', pagerEl) : [];
  let i = 0;
  setInterval(() => {
    slides[i].classList.remove('is-active');
    dots[i] && dots[i].classList.remove('is-active');
    i = (i + 1) % slides.length;
    slides[i].classList.add('is-active');
    dots[i] && dots[i].classList.add('is-active');
  }, interval);
}
fadeSlider('.hero-slide', $('#heroPager'));

// ---------- break: pinned scroll sequence ----------
// The section pins while the comp-ratio frame zooms (uniform scale only — the
// 1104x806 layout never distorts) to the largest size that fits below the
// header, then scroll steps through the three slides; the page only continues
// once the last slide has shown. The cover-sized background pans bottom-to-top
// (position-y 0%→100%) while the inset stack is pushed sideways one photo at a
// time; the backgrounds cross-fade on that same position, so both read as one
// scrubbed movement and reverse together on the way back up.
// Reduced motion falls back to the static layout + timed fades.
(() => {
  const sec = $('#break');
  const pin = $('.break-pin');
  const frame = $('.break-frame');
  const slides = $$('.break-slide');
  const dots = $$('#breakPager li');
  const insetImgs = $$('.break-inset-stack img');
  if (!sec || !pin || !frame || !slides.length) return;

  // ABOUT: 参考動画ではピン留めも全画面拡大もしない。通常スクロールで流れつつ
  //  ・背景スライドがスクロールに応じてゆっくりズームイン
  //  ・黒ボックスが背景より遅れて動く視差
  // の2つがかかる。TOP のピン演出はここでは使わない。
  if (sec.classList.contains('about-break')) {
    fadeSlider('.break-slide', $('#breakPager'));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const box = $('.manifesto', sec) || $('.break-overlay', sec);
    // SP は枠が小さく、PC と同じ振れ幅だと黒ボックスが枠から外れるので控えめに。
    // 効果を止めてしまうと視差そのものが消えるため、量だけ落とす。
    const isSP = window.matchMedia('(max-width: 767px)').matches;
    const ZOOM = isSP ? 0.20 : 0.42;   // 背景の最大ズーム量
    const RISE = isSP ? 26 : 90;       // 序盤に持ち上がる量(px)
    const DROP = isSP ? 60 : 210;      // その後に降りきる量(px)
    const EASE = 0.085;  // 慣性の追従率。小さいほど遅れてついてくる
    let target = 0, current = 0, targetZoom = 1, currentZoom = 1;
    let loop = 0, visible = false;

    // SP: 文章は1本にまとめてあり黒ボックスに収まらない。枠を画面中央に留め、
    // 縦スクロールの量をそのまま文章の横送りに変える（留める長さ = はみ出し量）。
    // 動きを減らす設定のときはここまで来ず、CSS 側の横スワイプで読める。
    const copy = $('.manifesto-copy', sec);
    const copyTxt = copy && $('.manifesto-txt', copy);
    const spQuery = window.matchMedia('(max-width: 767px)');
    const READ_HOLD = 60;  // 読み始めと読み終わりに止めておくスクロール量(レイアウトpx)
    // 文章を 1px 送るのに要る縦スクロール量。1 だと指の速さのまま流れて
    // 読み切れないので、ゆっくり送る（大きいほど遅く、留まる時間も長い）
    const READ_PACE = 1.8;
    // 送りの追従率。スクロール位置へ直結すると指の細かい揺れやイベントの
    // 間隔がそのまま出てカクつくので、目標へ少しずつ寄せる
    const READ_EASE = 0.16;
    let readOverflow = 0, readTop = 0, readStart = 0, readW = 0, readZoom = 1;
    let readTarget = 0, readCurrent = 0;
    // innerHeight や getBoundingClientRect は端末px、書き戻す値はレイアウトpx
    const zoomOf = () => parseFloat(getComputedStyle(document.body).zoom) || 1;
    const measureRead = () => {
      if (!copy) return;
      sec.classList.remove('js-read');
      sec.style.height = '';
      pin.style.top = '';
      // 送りの transform が残っていると、はみ出し量を短く測ってしまう
      if (copyTxt) copyTxt.style.transform = '';
      copy.scrollLeft = 0;
      readOverflow = spQuery.matches && copyTxt ? Math.max(0, copy.scrollWidth - copy.clientWidth) : 0;
      if (!readOverflow) { readTarget = readCurrent = 0; return; }
      readZoom = zoomOf();
      readTop = Math.round((window.innerHeight / readZoom - pin.offsetHeight) / 2);
      readStart = pin.offsetTop;
      pin.style.top = `${readTop}px`;
      sec.style.height = `${sec.offsetHeight + readOverflow * READ_PACE + READ_HOLD * 2}px`;
      sec.classList.add('js-read');
    };
    const applyRead = () => {
      if (!readOverflow) return;
      const done = readTop - sec.getBoundingClientRect().top / readZoom - readStart;
      const q = Math.max(0, Math.min(1, (done - READ_HOLD) / (readOverflow * READ_PACE)));
      // ここでは目標だけ決める。描画は tick() が transform で行う。scrollLeft は
      // 整数pxにしか動かず、ゆっくり送ると段々に進んで見えるため使わない
      readTarget = q * readOverflow;
    };

    // スクロール位置から「目標値」だけを求める。実際の描画は tick() が
    // そこへ徐々に近づけるので、指を止めてもワンテンポ遅れて追いつく。
    const measure = () => {
      const r = sec.getBoundingClientRect();
      const vh = window.innerHeight;
      visible = r.bottom > -240 && r.top < vh + 240;
      if (!visible) return;
      const p = Math.max(0, Math.min(1, (vh - r.top) / Math.max(1, vh + r.height)));

      targetZoom = 1 + p * ZOOM;
      // 目線を枠内に留めるため、まず少し上へ、その後ゆっくり下へ降りる。
      // sin カーブの前半で持ち上げ、全体では下向きへ流す。
      target = Math.sin(p * Math.PI) * -RISE + (p - 0.5) * DROP;
      // 文章を横に送っている間は黒ボックスを上下させない（読みにくくなる）
      if (readOverflow) target = 0;
    };

    const tick = () => {
      loop = 0;
      current += (target - current) * EASE;
      currentZoom += (targetZoom - currentZoom) * EASE;
      slides.forEach((s) => { s.style.transform = `scale(${currentZoom.toFixed(4)})`; });
      if (box) box.style.setProperty('--manifesto-shift', `${current.toFixed(1)}px`);
      if (readOverflow) {
        readCurrent += (readTarget - readCurrent) * READ_EASE;
        // rtl の窓で左へはみ出した文章を、右へずらして読み進める
        copyTxt.style.transform = `translate3d(${readCurrent.toFixed(2)}px, 0, 0)`;
      }
      // 目標に届くまで回し続ける（慣性の余韻）
      if (visible && (Math.abs(target - current) > 0.15 || Math.abs(targetZoom - currentZoom) > 0.0008
          || Math.abs(readTarget - readCurrent) > 0.05)) {
        loop = requestAnimationFrame(tick);
      }
    };

    const onScroll = () => { measure(); applyRead(); if (!loop) loop = requestAnimationFrame(tick); };
    // アドレスバーの出入り(高さだけの変化)で測り直すと留めた枠が跳ねるので、
    // 幅が変わったとき(回転など)だけ測り直す
    const remeasureRead = () => {
      const w = document.documentElement.clientWidth;
      if (w === readW) return;
      readW = w;
      measureRead();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { remeasureRead(); onScroll(); });
    remeasureRead();
    // フォントが入ると文章の幅が変わるので測り直す
    if (document.fonts) document.fonts.ready.then(() => { measureRead(); onScroll(); });
    measure();
    applyRead();
    current = target; currentZoom = targetZoom; readCurrent = readTarget;  // 初期表示は遅延させない
    tick();
    return;
  }

  const TRANS_W = 0.11;   // share of the sequence each push takes; the rest is
                          // split evenly so every photo gets the same still time
  const clamp = (v) => Math.max(0, Math.min(1, v));
  let active = 0, ticking = false;

  function setSlide(i) {
    if (i === active) return;
    slides[active].classList.remove('is-active');
    dots[active] && dots[active].classList.remove('is-active');
    active = i;
    slides[active].classList.add('is-active');
    dots[active] && dots[active].classList.add('is-active');
  }

  // Continuous position in the photo stack: 0 = first photo shown, 1 = second,
  // and so on. Still, push, still, push, still — with the dwells equal so the
  // middle photo reads as long as the outer two.
  function stackPos(u) {
    const steps = insetImgs.length - 1;
    if (steps < 1) return 0;
    const dwell = (1 - steps * TRANS_W) / insetImgs.length;
    let pos = 0;
    for (let k = 0; k < steps; k++) {
      pos += clamp((u - ((k + 1) * dwell + k * TRANS_W)) / TRANS_W);
    }
    return pos;
  }

  // q drives the background pan; u is the sequence progress and spans the whole
  // section, so all three slides get the same slice of scroll. The inset stack
  // shifts as one strip so the incoming photo pushes the outgoing one out, and
  // each background wipes in bottom-to-top on that same position — later slides
  // sit above earlier ones in the DOM, so revealing one covers the one before.
  // Scrolling back runs the whole thing in reverse for free.
  const FEATHER = 14;   // % of the frame height the wipe's soft edge spans
  function scrub(q, u) {
    const pan = `50% ${(q * 100).toFixed(2)}%`;
    const pos = stackPos(clamp(u));
    insetImgs.forEach((img, i) => {
      img.style.transform = `translateX(${((i - pos) * 100).toFixed(2)}%)`;
    });
    slides.forEach((s, i) => {
      s.style.backgroundPosition = pan;
      const reveal = clamp(pos - i + 1);
      s.style.setProperty('--reveal', `${(reveal * (100 + FEATHER) - FEATHER).toFixed(2)}%`);
    });
    setSlide(Math.round(pos));
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    fadeSlider('.break-slide', $('#breakPager'));
    // keep the inset stack in step with the pager, without the slide-up motion
    const pager = $('#breakPager');
    if (pager && insetImgs.length) {
      new MutationObserver(() => {
        const idx = [...pager.children].findIndex((li) => li.classList.contains('is-active'));
        insetImgs.forEach((img, i) => {
          img.style.transform = i <= idx ? 'translateX(0)' : '';
        });
      }).observe(pager, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    return;
  }

  // SP: the comp fixes this panel at 582.5px, so instead of the desktop runway
  // the panel sticks centred for a short hold while the slides step, then
  // releases. Progress spans the whole travel (enter → hold → exit), so the pan
  // keeps moving on the way in and out and only the slide steps land in the hold.
  if (window.matchMedia('(max-width: 767px)').matches) {
    const HOLD_VH = 2.4;               // how long the panel sits still, in viewport heights
    sec.classList.add('js-hold');

    const applySP = () => {
      ticking = false;
      const r = sec.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < 0 || r.top > vh) return;
      const q = clamp((vh - r.top) / (vh + r.height));
      scrub(q, q);
    };

    const measureSP = () => {
      // innerHeight is device px; the values we write back are layout px
      const z = parseFloat(getComputedStyle(document.body).zoom) || 1;
      const vh = window.innerHeight / z;
      // the sticky box is the panel itself, parked in the middle of the screen
      const panelH = frame.offsetHeight;
      const pinTop = Math.max(0, Math.round((vh - panelH) / 2));
      const secH = Math.round(panelH + vh * HOLD_VH);
      pin.style.height = panelH + 'px';
      pin.style.top = pinTop + 'px';
      sec.style.height = secH + 'px';
      applySP();
    };

    const tick = () => { if (!ticking) { ticking = true; requestAnimationFrame(applySP); } };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', measureSP);
    measureSP();
    return;
  }
  sec.classList.add('js-pin');

  const EXPAND_END = 0.25;         // scrub fraction spent on the fullscreen expansion
  const RUNWAY_VH = 3.4;           // extra scroll distance, in viewport heights

  const zoom = () => parseFloat(getComputedStyle(document.body).zoom) || 1;
  const header = $('#siteHeader');
  let designW = 0, designH = 0, scale0 = 1, scale1 = 1;

  function measure() {
    frame.style.width = '';
    frame.style.height = '';
    frame.style.transform = '';
    const z = zoom();
    // the header hides itself while pinned, so the stage is the full viewport
    const pinH = Math.round(window.innerHeight / z);
    pin.style.top = '0px';
    pin.style.height = pinH + 'px';
    designW = frame.offsetWidth;
    // SP: width:auto collapses inside the flex pin; the design width there is
    // the viewport minus the 12px gutters
    if (designW < 50) designW = pin.clientWidth - 24;
    designH = frame.offsetHeight;
    // the frame always keeps the comp size and ratio; only a uniform scale
    // animates. scale1 = the largest comp-ratio size that fits the stage.
    const fit = Math.min(window.innerWidth / z / designW, pinH / designH);
    scale0 = Math.min(1, fit);
    scale1 = fit;
    frame.style.width = designW + 'px';
    frame.style.height = designH + 'px';
    sec.style.height = Math.round(window.innerHeight / z * (1 + RUNWAY_VH)) + 'px';
    apply();
  }

  function apply() {
    ticking = false;
    const r = sec.getBoundingClientRect();
    const vh = window.innerHeight;
    // the header slides away while the section owns the viewport
    if (header) header.classList.toggle('is-break-hide', r.top <= 1 && r.bottom >= vh - 1);
    if (r.bottom < 0 || r.top > vh) return;
    const q = clamp(-r.top / (r.height - vh));

    // uniform zoom (ease-out) from the comp size to the largest fit — the
    // ratio and the inner layout never change
    const t = Math.min(1, q / EXPAND_END);
    const e = 1 - (1 - t) * (1 - t);
    frame.style.transform = `scale(${(scale0 + (scale1 - scale0) * e).toFixed(4)})`;

    // the sequence runs on enter→exit progress, not the pinned runway, so each
    // slide owns the same amount of scroll even with the scroll-in and release
    scrub(q, clamp((vh - r.top) / (vh + r.height)));
  }

  function onScroll() {
    if (!ticking) { ticking = true; requestAnimationFrame(apply); }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', measure);
  measure();
})();

const manifestoPopup = $('#manifestoPopup');
const manifestoPopupOpen = $('#manifestoPopupOpen');
if (manifestoPopup && manifestoPopupOpen) {
  const closeButton = $('.manifesto-popup-close', manifestoPopup);
  manifestoPopupOpen.addEventListener('click', () => {
    document.body.classList.add('is-manifesto-popup-open');
    if (typeof manifestoPopup.showModal === 'function') manifestoPopup.showModal();
    else manifestoPopup.setAttribute('open', '');
  });
  closeButton.addEventListener('click', () => manifestoPopup.close());
  manifestoPopup.addEventListener('close', () => {
    document.body.classList.remove('is-manifesto-popup-open');
  });
  manifestoPopup.addEventListener('click', event => {
    if (event.target === manifestoPopup) manifestoPopup.close();
  });
}

/* ============================================================
   ABOUT page: switchable SLIDE / LIST chapters
   ============================================================ */
// Photography for these rails comes from Shopify (articles, products, editor
// blocks). Before any of it exists the chapter still has to lay out, so every
// slot falls back to the theme's single shared placeholder.
const PLACEHOLDER = asset('placeholder.svg');
const aboutChapterData = {
  fieldlog: {
    images: [PLACEHOLDER],
    imageAlt: '作り手と味に出会う記録',
  },
  dictionary: {
    images: [PLACEHOLDER],
    listImages: [PLACEHOLDER],
    titles: ['ダミーテキスト・人参', 'ダミーテキスト・牡蠣', 'ダミーテキスト・ハム', 'ダミーテキスト・鮭', 'ダミーテキスト・アスパラガス', 'ダミーテキスト・玉ねぎ'],
    imageAlt: '食材',
  },
  abouteditor: {
    images: [PLACEHOLDER],
    imageAlt: '味覚エディター',
  },
};

// Each chapter section prints its real entries as JSON; the literal above is
// only the fallback for a store with no products or articles yet.
function chapterDataFor(type, chapter) {
  const tag = $(`[data-chapter-data="${type}"]`) || $('[data-chapter-data]', chapter);
  if (!tag) return aboutChapterData[type];
  let payload;
  try {
    payload = JSON.parse(tag.textContent);
  } catch (_) {
    return aboutChapterData[type];
  }
  if (!payload || !Array.isArray(payload.items) || !payload.items.length) {
    return aboutChapterData[type];
  }
  return {
    images: payload.items.map(item => item.image),
    listImages: payload.items.map(item => item.image),
    titles: payload.items.map(item => item.title),
    entries: payload.items,
    cardCopy: payload.cardCopy,
    imageAlt: payload.imageAlt || (aboutChapterData[type] || {}).imageAlt || '',
  };
}

$$('[data-about-chapter]').forEach(chapter => {
  const type = chapter.dataset.aboutChapter;
  const data = chapterDataFor(type, chapter);
  const track = $('.chapter-slide-track', chapter);
  const list = $('.chapter-list-view .v-list', chapter);
  const listItems = list ? $$('li', list) : [];
  const thumb = $('.chapter-list-view .chapter-img', chapter);
  const thumbImg = thumb ? $('img', thumb) : null;
  const counter = $('.frac-n', chapter);
  const denominator = $('.frac-d', chapter);
  const modeButtons = $$('[data-chapter-mode]', chapter);
  const navButtons = $$('[data-chapter-dir]', chapter);
  if (!data || !track || !listItems.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let mode = 'slide';
  let slideIndex = 0;
  let listIndex = Math.min(2, listItems.length - 1);
  let autoTimer = 0;
  let scrollFrame = 0;
  let listWrapLock = 0;

  const itemTitle = (item, index) => {
    if (data.entries && data.entries[index]) return data.entries[index].title;
    const text = item.textContent.replace(/\s+/g, ' ').trim();
    if (type === 'fieldlog') return 'タイトルが入ります。';
    if (type === 'dictionary') return data.titles[index % data.titles.length];
    return text.replace(/^#\d+\s*/, '') || `味覚エディター ${index + 1}`;
  };

  listItems.forEach((item, index) => {
    const entry = data.entries && data.entries[index];
    const image = entry ? entry.image : data.images[index % data.images.length];
    const number = String(index + 1).padStart(3, '0');
    const title = itemTitle(item, index);
    // No url means no link: the <a> stays (the card's whole treatment hangs
    // off it) but without href, so it neither navigates nor takes focus.
    const url = (entry && entry.url) || ($('a', item) && $('a', item).getAttribute('href')) || '';
    const href = url && url !== '#' ? ` href="${url}"` : '';
    const copy = (entry && entry.copy) || data.cardCopy || '';
    const tagList = (entry && entry.tags) || ['CATEGORY', 'CATEGORY'];
    const tagChips = tagList.slice(0, 3).map(tag => `<span>#${tag}</span>`).join('');
    const card = document.createElement('article');
    card.className = 'chapter-slide-card';

    if (type === 'fieldlog') {
      card.innerHTML = `
        <a${href}>
          <figure><img src="${image}" alt="${data.imageAlt} ${number}"></figure>
          <p class="chapter-card-index">JOURNAL #${number}</p>
          <h3 class="chapter-card-title">${title}</h3>
          <p class="chapter-card-copy">${copy || '作り手と食材の背景をたどる、味覚編集のフィールドログ。'}</p>
          <p class="chapter-card-tags">${tagChips}</p>
        </a>`;
    } else if (type === 'dictionary') {
      const dateLabel = (entry && entry.date) || '2026.<br>00.00';
      // 公開前は Coming Soon: リンクを外し、白帯に沿う縦書きバッジを重ねる
      const soon = chapter.hasAttribute('data-coming-soon');
      card.innerHTML = `
        <a${soon ? '' : href}>
          <figure><img src="${image}" alt="${title}">${soon ? '<span class="chapter-card-soon" aria-hidden="true"><b>Coming Soon...</b></span>' : ''}</figure>
          <p class="chapter-card-index">${dateLabel}</p>
          <h3 class="chapter-card-title">${title}</h3>
          <p class="chapter-card-tags">${tagChips}</p>
          <p class="chapter-card-copy">${copy || '味わいと旬、産地をひもとく食材図鑑。'}</p>
        </a>`;
    } else {
      // 味覚エディターのカードはリンクなし(hrefを付けない)
      card.innerHTML = `
        <a>
          <figure><span class="chapter-card-num">#${index + 1}</span><img src="${image}" alt="${title}"></figure>
          <h3 class="chapter-card-title">${title}</h3>
          <p class="chapter-card-copy">${copy || '肩書きが入ります。肩書きが入ります。'}</p>
        </a>`;
    }

    if (href === '#') {
      $('a', card).addEventListener('click', event => event.preventDefault());
    }
    track.appendChild(card);
  });

  const cards = $$('.chapter-slide-card', track);
  if (denominator) denominator.textContent = listItems.length;

  // 無限ループ: 実カードの後ろに複製を継ぎ足し、末尾まで来たら
  // 見た目が同じ先頭位置へ transition なしで差し替える。
  // これで「最後 → 最初」の逆走がなく、次のカードがそのまま繋がって見える。
  const realCount = cards.length;
  // 実カードが少ない時（記事1本など）でも送り先の右側が空かないよう、
  // 複製は最低3枚（同じカードの繰り返し）を確保する
  const CLONES = Math.min(Math.max(realCount, 3), 8);
  for (let i = 0; i < CLONES; i += 1) {
    const clone = cards[i % realCount].cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    const cloneLink = $('a', clone);
    if (cloneLink) {
      cloneLink.tabIndex = -1;
      cloneLink.addEventListener('click', event => event.preventDefault());
    }
    track.appendChild(clone);
  }
  const allCards = $$('.chapter-slide-card', track);

  function stepWidth() {
    const card = cards[0];
    if (!card) return 0;
    const style = getComputedStyle(track);
    return card.getBoundingClientRect().width + (parseFloat(style.columnGap || style.gap) || 0);
  }

  function updateCounter(index) {
    if (counter) counter.textContent = index + 1;
  }

  function applyOffset(index, animate = true) {
    const target = allCards[index];
    const offset = target
      ? target.offsetLeft - allCards[0].offsetLeft
      : index * stepWidth();
    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    if (!animate) {
      // 差し替えを確定させてから transition を戻す
      void track.offsetWidth;
      track.style.transition = '';
    }
  }

  // 複製ゾーンにいる間に次の送りが来たら、まず実カード位置へ黙って戻す。
  // 720ms の巻き戻しを待たずに進むと複製の先へ走り、カードが消えて見える
  let settleTimer = 0;
  function normalizeSlide() {
    if (slideIndex < realCount) return;
    window.clearTimeout(settleTimer);
    settleTimer = 0;
    slideIndex -= realCount;
    applyOffset(slideIndex, false);
  }

  function updateSlide(animate = true) {
    applyOffset(slideIndex, animate);
    updateCounter(slideIndex % realCount);
    // 複製ゾーンに入ったら、同じ見た目の実カード位置へ黙って巻き戻す
    window.clearTimeout(settleTimer);
    settleTimer = 0;
    if (slideIndex >= realCount) {
      const settle = () => {
        settleTimer = 0;
        slideIndex -= realCount;
        applyOffset(slideIndex, false);
        updateCounter(slideIndex % realCount);
      };
      settleTimer = window.setTimeout(settle, 720);
    }
  }

  function stopAuto() {
    window.clearInterval(autoTimer);
    autoTimer = 0;
  }

  function startAuto() {
    stopAuto();
    if (mode !== 'slide' || reducedMotion || document.hidden) return;
    if (realCount < 2) return;
    autoTimer = window.setInterval(() => {
      // % を取らずに前進させ、複製ゾーンで updateSlide が巻き戻す
      normalizeSlide();
      slideIndex += 1;
      updateSlide();
    }, 5200);
  }

  function updateList(index, shouldScroll = false) {
    // 端で止めず循環させる（-1 → 末尾、length → 先頭）
    const len = listItems.length;
    listIndex = ((index % len) + len) % len;
    listItems.forEach((item, itemIndex) => {
      item.classList.toggle('is-thumb-active', itemIndex === listIndex);
    });

    const activeItem = listItems[listIndex];
    const listImages = data.listImages || data.images;
    const image = listImages[listIndex % listImages.length];
    if (thumbImg) {
      // Liquid の image_tag が出力した srcset が残っていると src を替えても
      // 表示が切り替わらないので、差し替え時に外す
      thumbImg.removeAttribute('srcset');
      thumbImg.removeAttribute('sizes');
      thumbImg.src = image;
      thumbImg.alt = `${data.imageAlt} ${listIndex + 1}`;
    }

    if (shouldScroll) {
      activeItem.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    if (shouldScroll) {
      requestAnimationFrame(() => updateCounter(indexAtListStart()));
    }
    // 循環するので端でも押せる（disabled にしない）
    navButtons.forEach(button => { button.disabled = false; });
  }

  function indexAtListStart() {
    const listRect = list.getBoundingClientRect();
    let nearest = 0;
    let nearestDistance = Infinity;
    listItems.forEach((item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - listRect.left - 12);
      if (distance < nearestDistance) {
        nearest = index;
        nearestDistance = distance;
      }
    });
    return nearest;
  }

  function setMode(nextMode) {
    mode = nextMode === 'list' ? 'list' : 'slide';
    chapter.classList.toggle('is-slide-mode', mode === 'slide');
    chapter.classList.toggle('is-list-mode', mode === 'list');
    modeButtons.forEach(button => {
      const active = button.dataset.chapterMode === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    if (mode === 'slide') {
      navButtons.forEach(button => { button.disabled = false; });
      updateSlide();
      startAuto();
    } else {
      stopAuto();
      requestAnimationFrame(() => {
        updateList(listIndex);
        updateCounter(indexAtListStart());
      });
    }
  }

  modeButtons.forEach(button => {
    button.addEventListener('click', () => setMode(button.dataset.chapterMode));
  });

  navButtons.forEach(button => {
    button.addEventListener('click', () => {
      if (mode === 'slide' && realCount < 2) return;
      const dir = Number(button.dataset.chapterDir) || 1;
      if (mode === 'slide') {
        if (dir > 0) normalizeSlide();
        if (dir < 0 && slideIndex === 0) {
          // 先頭で「前へ」: いったん複製側の同位置へ黙って跳んでから戻すと
          // 逆走せずに末尾のカードが繋がって出てくる
          slideIndex = realCount;
          applyOffset(slideIndex, false);
        }
        slideIndex += dir;
        updateSlide();
        startAuto();
      } else {
        const next = listIndex + dir;
        // 端をまたぐときは scrollIntoView の慣性が収まるまで同期を止める
        if (next < 0 || next >= listItems.length) {
          window.clearTimeout(listWrapLock);
          listWrapLock = window.setTimeout(() => { listWrapLock = 0; }, 700);
        }
        updateList(next, true);
      }
    });
  });

  listItems.forEach((item, index) => {
    const link = $('a', item);
    item.addEventListener('mouseenter', () => updateList(index));
    link.addEventListener('focus', () => updateList(index));
    link.addEventListener('click', event => event.preventDefault());
  });

  list.addEventListener('scroll', () => {
    // ナビで端をまたいだ直後は scrollIntoView 由来の scroll が走るため、
    // その間だけ同期を止める（止めないと循環後の位置を上書きしてしまう）
    if (mode !== 'list' || scrollFrame || listWrapLock) return;
    scrollFrame = requestAnimationFrame(() => {
      const leadingIndex = indexAtListStart();
      updateList(leadingIndex);
      updateCounter(leadingIndex);
      scrollFrame = 0;
    });
  }, { passive: true });

  // LIST mode intentionally converts vertical wheel movement into the required
  // horizontal journey. At either end, normal page scrolling resumes.
  list.addEventListener('wheel', event => {
    if (mode !== 'list') return;
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    const maxScroll = list.scrollWidth - list.clientWidth;
    const atStart = list.scrollLeft <= 1;
    const atEnd = list.scrollLeft >= maxScroll - 1;
    if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
    event.preventDefault();
    list.scrollLeft += delta;
  }, { passive: false });

  const viewport = $('.chapter-slide-viewport', chapter);
  if (viewport) {
    viewport.addEventListener('mouseenter', stopAuto);
    viewport.addEventListener('mouseleave', startAuto);
    viewport.addEventListener('focusin', stopAuto);
    viewport.addEventListener('focusout', startAuto);
  }

  window.addEventListener('resize', () => {
    if (mode === 'slide') updateSlide();
    else {
      updateList(listIndex);
      updateCounter(indexAtListStart());
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopAuto();
    else startAuto();
  });

  updateList(listIndex);
  setMode('slide');
});

// 黒ボックスの視差は上の about-break ブロックが --manifesto-shift を
// 書き込んで一括で扱う。ここに ±34px にクランプする別処理があると
// そちらが後勝ちして視差がほぼ効かなくなるため、統合済み。

// ---------- swipe ----------
// Touch and pen only. The cards carry links and body copy, so a mouse drag here
// would fight text selection; the arrows already serve pointer users.
// Direction is decided on the first few pixels: a vertical move is handed
// straight back to the page, and a horizontal one is captured for the rest of
// the gesture. The tracks set touch-action: pan-y so the browser keeps
// scrolling vertically without swallowing the horizontal move.
const SWIPE_AXIS = 8;    // px before the gesture commits to an axis
const SWIPE_TAKE = 40;   // px that counts as "next card"
const SWIPE_FLICK = 0.4; // px/ms that counts even when the distance is short

function addSwipe(el, { onDrag, onEnd, pause, resume } = {}) {
  if (!el) return;
  let id = null, x0 = 0, y0 = 0, t0 = 0, horizontal = false, swiped = false;
  const reset = () => { id = null; horizontal = false; };

  el.addEventListener('pointerdown', e => {
    if (e.pointerType === 'mouse' || id !== null) return;
    id = e.pointerId; x0 = e.clientX; y0 = e.clientY; t0 = e.timeStamp;
    horizontal = false;
  });

  el.addEventListener('pointermove', e => {
    if (e.pointerId !== id) return;
    const dx = e.clientX - x0, dy = e.clientY - y0;
    if (!horizontal) {
      if (Math.abs(dx) < SWIPE_AXIS && Math.abs(dy) < SWIPE_AXIS) return;
      if (Math.abs(dy) >= Math.abs(dx)) { reset(); return; }  // let the page scroll
      horizontal = true;
      swiped = true;
      try { el.setPointerCapture(id); } catch (_) { /* already released */ }
      pause?.();
    }
    onDrag?.(dx);
  });

  const settle = e => {
    if (e.pointerId !== id) return;
    const wasHorizontal = horizontal;
    let dir = 0;
    if (wasHorizontal && e.type === 'pointerup') {
      const dx = e.clientX - x0;
      const speed = Math.abs(dx) / Math.max(1, e.timeStamp - t0);
      if (Math.abs(dx) > SWIPE_TAKE || speed > SWIPE_FLICK) dir = dx < 0 ? 1 : -1;
    }
    reset();
    onEnd?.(dir);
    if (wasHorizontal) resume?.();
  };
  el.addEventListener('pointerup', settle);
  el.addEventListener('pointercancel', settle);

  // The gesture usually ends on top of a card, whose whole surface is a link.
  el.addEventListener('click', e => {
    if (!swiped) return;
    swiped = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);
}

// ---------- track sliders (picks / journal / seasonal / gift) ----------
function trackSlider(name, trackSel, counterId, { loop = false, autoMs = 0, perMove = 1 } = {}) {
  const track = $(trackSel);
  if (!track) return;
  const cards = [...track.children].filter(el => el.tagName === 'ARTICLE');
  if (!cards.length) return;
  const counter = document.getElementById(counterId);
  const total = cards.length;
  const steps = Math.ceil(total / perMove);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 1送り分以下しかカードがない時は動かすものがない。ループ複製と
  // 自動送りを止めないと、複製へ滑り続けてスペーサー分ズレて見える
  if (steps <= 1) { loop = false; autoMs = 0; }

  // A trailing copy of the whole track lets index === steps render as "back to the
  // start", so the jump home happens off-screen and the loop reads as continuous.
  // Cloning every child, not just the cards, preserves any spacers between them.
  if (loop) {
    [...track.children].forEach(el => {
      const clone = el.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  let index = 0;

  // Measuring across `perMove` cards picks up gaps and spacers for free.
  function step() {
    if (cards[perMove]) return cards[perMove].offsetLeft - cards[0].offsetLeft;
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return cards[0].offsetWidth + gap;
  }

  // Follows the finger during a swipe; 0 at rest.
  let dragDx = 0;
  const offset = () => -index * step() + dragDx;

  function render(animate = true) {
    if (!animate) {
      track.style.transition = 'none';
      track.style.transform = `translateX(${offset()}px)`;
      void track.offsetWidth; // commit the jump before transitions come back
      track.style.transition = '';
    } else {
      track.style.transform = `translateX(${offset()}px)`;
    }
    if (counter) counter.textContent = (((index % steps) + steps) % steps) * perMove + 1;
  }

  // Positions `total` and 0 (and `total - 1` and -1) show identical content, so
  // rebasing between them is invisible. Doing it just before the next move —
  // rather than on transitionend, which can be skipped or throttled — keeps the
  // index bounded no matter how the previous slide ended.
  function go(dir) {
    if (!loop) {
      index = Math.max(0, Math.min(index + dir, steps - 1));
      render();
      return;
    }
    if (index >= steps) { index -= steps; render(false); }
    if (dir < 0 && index === 0) { index = steps; render(false); }
    index = Math.max(0, Math.min(index + dir, steps));
    render();
  }

  let timer = null;
  function stopAuto() { clearInterval(timer); timer = null; }
  function startAuto() {
    if (!autoMs || reducedMotion || timer) return;
    timer = setInterval(() => { if (!document.hidden) go(1); }, autoMs);
  }

  $$(`[data-slider="${name}"]`).forEach(btn => {
    btn.addEventListener('click', () => {
      go(btn.classList.contains('next') ? 1 : -1);
      stopAuto();
      startAuto(); // manual input restarts the dwell instead of cutting it short
    });
  });
  addSwipe(track, {
    pause: () => { stopAuto(); track.style.transition = 'none'; },
    resume: startAuto,
    onDrag: dx => { dragDx = dx; track.style.transform = `translateX(${offset()}px)`; },
    onEnd: dir => {
      dragDx = 0;
      track.style.transition = '';
      if (dir) go(dir); else render();
    },
  });
  track.addEventListener('mouseenter', stopAuto);
  track.addEventListener('mouseleave', startAuto);
  window.addEventListener('resize', () => render(false));
  startAuto();
}
trackSlider('picks', '.picks-track', 'picksNow', { loop: true, autoMs: 5000 });
trackSlider('journal', '.journal-track', 'journalNow', { loop: true, autoMs: 5000, perMove: 2 });
trackSlider('seasonal', '.seasonal-track', 'seasonalNow', { loop: true, autoMs: 5000 });
trackSlider('gift', '.gift-track', 'giftNow', { loop: true, autoMs: 5000 });

// ---------- centered carousel factory (editors / voice) ----------
function centerCarousel(trackEl, { counter, counterValues, navSel, start, loop = false, autoMs = 0, alternateSize = false } = {}) {
  const originals = [...trackEl.children];
  const n = originals.length;
  // loop mode: one clone set on each side; crossing an edge rebases by n cards
  // (the exact same layout one set over) before the animated move
  if (loop) {
    originals.forEach((c) => {
      const pre = c.cloneNode(true);
      const post = c.cloneNode(true);
      [pre, post].forEach((cl) => {
        cl.classList.add('is-clone');
        cl.setAttribute('aria-hidden', 'true');
        cl.removeAttribute('id');
      });
      trackEl.insertBefore(pre, originals[0]);
      trackEl.appendChild(post);
    });
  }
  const cards = [...trackEl.children];
  let pos = (loop ? n : 0) + (start ?? Math.floor(n / 2));
  let baseW = 0, centerW = 0, timer = 0;
  // alternateSize: every 2nd card out from the center shares the large size
  function setClasses() {
    cards.forEach((c, i) => {
      c.classList.toggle('is-center', i === pos);
      if (alternateSize) c.classList.toggle('is-major', (i + pos) % 2 === 0);
    });
  }
  // Settled (post-transition) card widths, measured with animations suppressed.
  // The slide target must use these: card sizes animate alongside the slide,
  // so live offsetWidth mid-flight would aim the translate at a stale layout.
  function readSizes() {
    trackEl.classList.add('no-anim');
    setClasses();
    void trackEl.offsetWidth;
    centerW = cards[pos].offsetWidth;
    baseW = (cards[pos + 1] || cards[pos - 1] || cards[pos]).offsetWidth;
    // Card heights animate between the base and major sizes, so the row is at
    // its shortest halfway through a slide — the viewport shrank by 14px on the
    // desktop board and everything below it moved, on every step and again on
    // every autoplay tick. Pin the viewport to the tallest settled card so the
    // page keeps still; the cards go on resizing inside it.
    const viewport = trackEl.parentElement;
    viewport.style.minHeight = '';
    cards.forEach(c => c.classList.add('is-major'));
    void trackEl.offsetWidth;
    const tallest = viewport.offsetHeight;
    setClasses();
    void trackEl.offsetWidth;
    viewport.style.minHeight = `${tallest}px`;
    trackEl.classList.remove('no-anim');
  }
  function update() {
    setClasses();
    const gap = parseFloat(getComputedStyle(trackEl).gap) || 0;
    // cards left of center settle at the base width; with alternateSize every
    // 2nd one of them (same parity as the center) settles at the large width
    const majorLeft = alternateSize ? Math.floor(pos / 2) : 0;
    const offset = majorLeft * centerW + (pos - majorLeft) * baseW + pos * gap;
    const wrap = trackEl.parentElement.offsetWidth;
    trackEl.style.transform = `translateX(${wrap / 2 - centerW / 2 - offset + dragDx}px)`;
    if (counter) counter.textContent = counterValues ? counterValues[pos % n] : (pos % n) + 1;
  }
  // Follows the finger during a swipe; 0 at rest.
  let dragDx = 0;
  function jumpTo(p) {
    pos = p;
    trackEl.classList.add('no-anim');
    update();
    void trackEl.offsetWidth;
    trackEl.classList.remove('no-anim');
  }
  function step(dir) {
    if (loop) {
      if (pos + dir >= n * 2) jumpTo(pos - n);
      else if (pos + dir < n) jumpTo(pos + n);
      pos += dir;
    } else {
      pos = Math.max(0, Math.min(pos + dir, cards.length - 1));
    }
    update();
  }
  function restartAuto() {
    if (!autoMs || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearInterval(timer);
    timer = setInterval(() => { if (!document.hidden) step(1); }, autoMs);
  }
  function remeasure() {
    readSizes();
    update();
  }
  if (navSel) $$(navSel).forEach(btn =>
    btn.addEventListener('click', () => { step(btn.classList.contains('next') ? 1 : -1); restartAuto(); }));
  trackEl.addEventListener('click', e => {
    const btn = e.target.closest('.arrow');
    if (btn && trackEl.contains(btn) && !btn.closest('[data-slider]')) {
      step(btn.classList.contains('next') ? 1 : -1);
      restartAuto();
    }
  });
  addSwipe(trackEl, {
    pause: () => { clearInterval(timer); trackEl.classList.add('no-anim'); },
    resume: restartAuto,
    onDrag: dx => { dragDx = dx; update(); },
    onEnd: dir => {
      dragDx = 0;
      trackEl.classList.remove('no-anim');
      if (dir) step(dir); else update();
    },
  });
  const hoverEl = trackEl.parentElement;
  hoverEl.addEventListener('mouseenter', () => clearInterval(timer));
  hoverEl.addEventListener('mouseleave', restartAuto);
  window.addEventListener('resize', remeasure);
  window.addEventListener('load', remeasure);
  remeasure();
  restartAuto();
}

// ---------- editors carousel (TOP) ----------
const editorsTrack = $('#editorsTrack');
if (editorsTrack && $$('.editor-card', editorsTrack).length) {
  // The section renders the cards, so this only wires the carousel to them.
  const cards = $$('.editor-card', editorsTrack);
  const startAttr = parseInt(editorsTrack.dataset.start, 10);
  const autoMs = parseInt(editorsTrack.dataset.autoMs, 10);
  centerCarousel(editorsTrack, {
    counter: $('#editorsNow'),
    counterValues: cards.map((card, i) => card.dataset.num || String(i + 1)),
    navSel: '[data-slider="editors"]',
    start: Number.isFinite(startAttr) ? Math.min(startAttr, cards.length - 1) : 0,
    loop: true,
    autoMs: Number.isFinite(autoMs) ? autoMs : 5000,
    alternateSize: true,
  });

  // +Detail opens the editor popup (AI top artboard 12 / SP artboard 18).
  // Delegated so it keeps working as centerCarousel clones and reorders cards.
  const editorPopup = $('#editorPopup');
  if (editorPopup) {
    const popImg = $('#edPopImg', editorPopup);
    const popName = $('#edPopName', editorPopup);
    const popLatin = $('#edPopLatin', editorPopup);
    let opener = null;

    const closeEditorPopup = () => editorPopup.close();

    const popTitle = $('#edPopTitle', editorPopup);
    const popUrl = $('#edPopUrl', editorPopup);
    const popUrlLink = $('#edPopUrlLink', editorPopup);
    const popLead = $('#edPopLead', editorPopup);
    const popProfile = $('#edPopProfile', editorPopup);
    const popProfileH = $('#edPopProfileH', editorPopup);
    const popMessage = $('#edPopMessage', editorPopup);
    const popMessageH = $('#edPopMessageH', editorPopup);
    const popTags = $('#edPopTags', editorPopup);
    const popMore = $('#edPopMore', editorPopup);

    // Show a field only when the block actually carries it, so a sparsely
    // filled editor does not leave an empty "Profile" heading behind.
    const setField = (heading, body, value) => {
      if (body) body.textContent = value || '';
      const shown = Boolean(value);
      if (body) body.hidden = !shown;
      if (heading) heading.hidden = !shown;
    };

    editorsTrack.addEventListener('click', event => {
      // Detail チップに加えて、カードの画像クリックでも同じポップアップを開く
      const chip = event.target.closest('.detail-chip');
      const figure = event.target.closest('.editor-card figure');
      if (!chip && !figure) return;
      event.preventDefault();
      const card = (chip || figure).closest('.editor-card');
      const img = card && $('img', card);
      const name = card && $('h3', card);
      const latin = card && $('.ec-name-v', card);
      const ep = (card && card.dataset) || {};
      if (popImg) {
        popImg.removeAttribute('srcset');
        popImg.removeAttribute('sizes');
        popImg.src = ep.epImage || (img && img.src) || popImg.src;
        popImg.alt = (name && name.textContent) || '';
      }
      if (popName && name) popName.textContent = name.textContent;
      if (popLatin && latin) popLatin.textContent = latin.textContent;
      if (popTitle) {
        popTitle.textContent = ep.epTitle || '';
        popTitle.hidden = !ep.epTitle;
      }
      if (popUrl && popUrlLink) {
        if (ep.epUrl) {
          popUrlLink.href = ep.epUrl;
          // 表記はプロトコル・末尾スラッシュ抜きで簡潔に
          popUrlLink.textContent = ep.epUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
        }
        popUrl.hidden = !ep.epUrl;
      }
      if (popLead) {
        popLead.textContent = ep.epLead || '';
        popLead.hidden = !ep.epLead;
      }
      setField(popProfileH, popProfile, ep.epProfile);
      setField(popMessageH, popMessage, ep.epMessage);
      if (popTags) {
        const tags = (ep.epTags || '').split(',').map(s => s.trim()).filter(Boolean);
        popTags.innerHTML = tags.map(tag => `<li>#${tag}</li>`).join('');
        popTags.hidden = !tags.length;
      }
      if (popMore && card && card.id) popMore.href = `#${card.id}`;
      opener = chip;
      document.body.classList.add('is-editor-popup-open');
      if (typeof editorPopup.showModal === 'function') editorPopup.showModal();
      else editorPopup.setAttribute('open', '');
    });

    $('#edPopClose', editorPopup).addEventListener('click', closeEditorPopup);
    // clicks land on the dialog box itself only when they fall on the backdrop
    editorPopup.addEventListener('click', event => {
      if (event.target === editorPopup) closeEditorPopup();
    });
    editorPopup.addEventListener('close', () => {
      document.body.classList.remove('is-editor-popup-open');
      if (opener && opener.isConnected) opener.focus();
      opener = null;
    });
  }
}

// ---------- voice carousel ----------
const voiceTrack = $('#voiceTrack');
if (voiceTrack && $$('.voice-card', voiceTrack).length) {
  const voiceAuto = parseInt(voiceTrack.dataset.autoMs, 10);
  centerCarousel(voiceTrack, { loop: true, autoMs: Number.isFinite(voiceAuto) ? voiceAuto : 5000 });
}

// ---------- 食材図鑑 accordions ----------
// Only rows whose article metafield produced a body render a real <button>;
// sub rows keep an inert <span> until they carry their own card, so nothing
// is wired for them.
$$('.zk-acc').forEach(sec => {
  const btn = $('.zk-acc-h button.zk-acc-btn', sec);
  const body = $(':scope > .zk-acc-body', sec);
  if (btn && body) {
    btn.addEventListener('click', () => {
      const open = sec.classList.toggle('is-open');
      body.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    });
  }
  $$('.zk-sub li', sec).forEach(li => {
    const subBtn = $('button.zk-acc-btn', li);
    const card = $('.zk-sub-body', li);
    if (!subBtn || !card) return;
    subBtn.addEventListener('click', () => {
      const open = li.classList.toggle('is-open');
      card.hidden = !open;
      subBtn.setAttribute('aria-expanded', String(open));
    });
  });
});

// ---------- 食材図鑑: 関連商品以下が記事の上へ被さるスクロール ----------
(() => {
  const stage = $('.zk-stage');
  if (!stage) return;
  const update = () => {
    const zoom = parseFloat(document.body.style.zoom) || 1;
    const vh = window.innerHeight / zoom;
    // 記事の下端がビューポート下端に達したところで止める（短い時は 0）
    stage.style.setProperty('--zk-stage-top', Math.min(0, vh - stage.offsetHeight) + 'px');
  };
  update();
  window.addEventListener('resize', update);
  if (window.ResizeObserver) new ResizeObserver(update).observe(stage);
})();

// ---------- フィールドログ（article.fieldlog） ----------
if (document.body.classList.contains('page-fieldlog')) {
  const zoom = () => parseFloat(getComputedStyle(document.body).zoom) || 1;
  const isSP = () => window.matchMedia('(max-width: 767px)').matches;
  const root = document.documentElement;
  const hero = $('#flHero');
  const photo = $('#flHeroPhoto');
  const article = $('#flArticle');
  const rail = $('#flRail');
  const yomu = $('#flYomu');
  const arrow = $('#flArrow');
  const producers = $$('.fl-producer');

  // .ai 実測（デザインpx）
  //  railPage : パンくず上端のページ座標（スクロール0のとき）
  //  railMin  : 画面上部に留まる位置（02_scroll_2 以降）
  //  yomuOff  : 写真の見えている下端から赤括弧グループ上端までの距離
  //  yomuMin  : 赤括弧が画面上部に留まる位置（02_scroll_3 以降）
  const comp = () => (isSP()
    ? { railPage: 73.5, railMin: 17.8, yomuOff: 91.3, yomuMin: 20.6 }
    : { railPage: 185.7, railMin: 40.4, yomuOff: 190.4, yomuMin: 39.8 });

  let ticking = false;
  const place = () => {
    ticking = false;
    const z = zoom();
    const c = comp();
    const sY = window.scrollY / z;
    if (rail) rail.style.top = `${Math.max(c.railMin, c.railPage - sY)}px`;
    const ar = article.getBoundingClientRect();
    let bottom = photo.getBoundingClientRect().bottom / z;
    // 本文パネルが写真を覆った分だけ「見えている下端」が上がる。SP はヒーローが
    // 画面に留まるので、これが無いと写真が画面内に残る縦長の端末ほど赤括弧が
    // 下で止まり、カンプの位置(yomuMin)まで上がらない
    bottom = Math.min(bottom, ar.top / z);
    if (yomu) {
      const top = bottom - c.yomuOff;
      yomu.style.top = `${Math.max(c.yomuMin, top)}px`;
      yomu.classList.toggle('is-clamped', top < c.yomuMin);
    }
    // 資料: 矢印はスクロールがはじまったら上向きに変化
    if (arrow) arrow.classList.toggle('is-up', sY > 40);
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(place);
  };
  // body.zoom 下では 100vh が二重に拡大されるので、実ビューポート高をデザインpxで持つ
  const layout = () => {
    const z = zoom();
    const vh = window.innerHeight / z;
    root.style.setProperty('--fl-vh', `${vh}px`);
    root.style.setProperty('--fl-lb-h', `${Math.round(vh * 0.76)}px`);
    // 資料 03: PC は右の写真が画面上端で止まり（写真上端 = ヒーロー内 58.7）、リード文が
    // 表示され切るまで左面がスクロールする。窓が低いときはリード文の下端を優先。
    // SP はリード文の下端が画面下端に来る位置で止まる。
    const heroLimit = isSP() ? 0 : -58.7;
    hero.style.top = `${Math.min(heroLimit, vh - hero.offsetHeight)}px`;
    // 資料 04: 作り手紹介も同じ要領で止まり、関連商品(や次の作り手)が上に重なる
    producers.forEach(p => { p.style.top = `${Math.min(0, vh - p.offsetHeight)}px`; });
    place();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', layout);
  window.addEventListener('load', layout);
  layout();

  if (arrow) {
    arrow.addEventListener('click', () => {
      if (arrow.classList.contains('is-up')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const top = article.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  }

  // 前後記事（PREV / NEXT）はまだ遷移先がないダミー。href="#" のままだと
  // ページ先頭へスクロールしてしまうので、記事が用意されるまで無効化しておく。
  $$('.fl-related a[href="#"]').forEach(a => a.addEventListener('click', e => e.preventDefault()));

  // 注釈: PC はマウスオーバーでポップアップ（CSS）、クリックで下部の注釈欄へ。
  // SP はタップで注釈欄へ移動（Wikipedia と同じ挙動、資料 10）。

  // 本文はエディタのリッチテキスト。素の <img> を記事写真（キャプション+
  // 拡大ボタン付きの fl-fig）へ組み替える。同じ段落に2枚 = 2枚並び、
  // 段落を分ければ縦積み。キャプションは画像の代替テキスト。
  $$('.fl-body p').forEach(p => {
    const imgs = [...p.querySelectorAll('img')].filter(i => !i.closest('.fl-fig, .fl-side, .fl-photo'));
    if (!imgs.length) return;
    const el = (tag, cls) => { const n = document.createElement(tag); if (cls) n.className = cls; return n; };
    const boxOf = img => {
      const box = el('div', 'fl-fig-img');
      const btn = el('button', 'fl-fig-open');
      btn.type = 'button';
      btn.setAttribute('aria-label', '写真を拡大');
      box.append(img, btn);
      return box;
    };
    const capOf = img => {
      if (!img.alt) return null;
      const c = el('figcaption');
      c.textContent = img.alt;
      return c;
    };
    let fig;
    if (imgs.length >= 2) {
      fig = el('figure', 'fl-fig fl-fig-two');
      imgs.forEach(img => {
        const item = el('div', 'fl-fig-item');
        item.append(boxOf(img));
        const c = capOf(img);
        if (c) item.append(c);
        fig.append(item);
      });
    } else {
      fig = el('figure', 'fl-fig');
      fig.append(boxOf(imgs[0]));
      const c = capOf(imgs[0]);
      if (c) fig.append(c);
    }
    if (p.textContent.trim() === '') p.replaceWith(fig);
    else p.after(fig);
  });

  // 写真ポップアップ（03_popup）
  const lightbox = $('#flLightbox');
  if (lightbox) {
    const lbImg = $('#flLightboxImg');
    const lbCap = $('#flLightboxCap');
    let opener = null;
    const open = (img, caption, from) => {
      lbImg.src = img.currentSrc || img.src;
      lbImg.alt = img.alt;
      lbCap.textContent = caption;
      opener = from;
      document.body.classList.add('is-fl-lightbox-open');
      if (typeof lightbox.showModal === 'function') lightbox.showModal();
      else lightbox.setAttribute('open', '');
    };
    const close = () => {
      if (typeof lightbox.close === 'function' && lightbox.open) lightbox.close();
      else lightbox.removeAttribute('open');
    };
    $$('.fl-fig-img').forEach(box => {
      const img = $('img', box);
      const wrap = box.closest('.fl-fig-item') || box.closest('.fl-fig');
      const cap = wrap && $('figcaption', wrap);
      const caption = cap ? cap.textContent.trim() : '';
      $('.fl-fig-open', box)?.addEventListener('click', e => open(img, caption, e.currentTarget));
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', () => open(img, caption, img));
    });
    $('#flLightboxClose').addEventListener('click', close);
    lightbox.addEventListener('click', e => {
      if (!e.target.closest('.fl-lightbox-fig')) close();
    });
    lightbox.addEventListener('close', () => {
      document.body.classList.remove('is-fl-lightbox-open');
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    });
  }
}


// ---------- top button ----------
const topBtn = $('#topBtn');
if (topBtn) topBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));

// ---------- reveal on scroll ----------
const revealObserver = new IntersectionObserver(entries => {
  entries.forEach(e => {
    if (e.isIntersecting) { e.target.classList.add('is-shown'); revealObserver.unobserve(e.target); }
  });
}, { threshold: .15 });
// The slider cards are deliberately absent: sliding them in from below fights
// the carousel's own horizontal motion.
$$('.reveal, .product-card').forEach(el => {
  el.classList.add('reveal');
  revealObserver.observe(el);
});
// maker-card はフェードイン(.reveal)なし。SP のグレー→カラー化に使う
// .is-shown を得るために観測だけ続ける。PC は一度ホバーしたらカラーを維持
$$('.maker-card').forEach(el => {
  revealObserver.observe(el);
  el.addEventListener('mouseenter', () => el.classList.add('is-colored'), { once: true });
});

/* ============================================================
   SELECT page
   ============================================================ */
// tabs: products / makers
const tabProducts = $('#tabProducts');
const tabMakers = $('#tabMakers');
if (tabProducts && tabMakers) {
  const panels = { products: $('#panelProducts'), makers: $('#panelMakers') };
  function activate(which) {
    tabProducts.classList.toggle('is-active', which === 'products');
    tabMakers.classList.toggle('is-active', which === 'makers');
    panels.products.hidden = which !== 'products';
    panels.makers.hidden = which !== 'makers';
  }
  tabProducts.addEventListener('click', () => activate('products'));
  tabMakers.addEventListener('click', () => activate('makers'));
}

// pagination (visual only)
$$('.page-num').forEach(btn => {
  btn.addEventListener('click', () => {
    $$('.page-num').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
  });
});

// SP fixed bar: sort dropdown (SP_select_表示順切り替え)
const spSortBtn = $('#spSortBtn');
if (spSortBtn) {
  const wrap = $('#spSortWrap');
  const drop = $('#spSortDrop');
  const label = $('#spSortLabel');
  function setSortOpen(open) {
    drop.hidden = !open;
    wrap.classList.toggle('is-open', open);
    spSortBtn.setAttribute('aria-expanded', String(open));
  }
  spSortBtn.addEventListener('click', () => setSortOpen(drop.hidden));
  $$('button', drop).forEach(b => b.addEventListener('click', () => {
    label.textContent = b.textContent;
    setSortOpen(false);
  }));
  document.addEventListener('click', e => {
    if (!drop.hidden && !wrap.contains(e.target)) setSortOpen(false);
  });
}

// SP filter overlay (SP_select_filtermenu_close / open)
const spFilter = $('#spFilter');
if (spFilter) {
  function showFilter(open) {
    spFilter.hidden = !open;
    document.documentElement.classList.toggle('is-spfilter-open', open);
    if (open) spFilter.scrollTop = 0;
  }
  $('#spFilterOpen').addEventListener('click', () => showFilter(true));
  $('#spFilterClose').addEventListener('click', () => showFilter(false));
  $('#spfApply').addEventListener('click', () => showFilter(false));
  // picking an option closes the accordion and shows the pick in its head
  $$('.spf-acc', spFilter).forEach(acc => {
    const jp = $('summary .jp', acc);
    const en = $('summary .en', acc);
    $$('.spf-opts button', acc).forEach(b => b.addEventListener('click', () => {
      jp.textContent = $('.jp', b).textContent;
      en.textContent = $('.en', b).textContent;
      acc.open = false;
    }));
  });
  $$('.spf-toggle', spFilter).forEach(t => t.addEventListener('click', () => {
    t.setAttribute('aria-checked', String(t.getAttribute('aria-checked') !== 'true'));
  }));
  $('#spfReset').addEventListener('click', () => {
    $$('.spf-acc', spFilter).forEach(acc => {
      acc.open = false;
      $('summary .jp', acc).textContent = acc.dataset.defaultJp;
      $('summary .en', acc).textContent = acc.dataset.defaultEn;
    });
    $$('.spf-toggle', spFilter).forEach(t => t.setAttribute('aria-checked', t.dataset.default));
  });
}

/* ============================================================
   SELECT DETAIL page
   ============================================================ */
// gallery
const galleryMain = $('#galleryMain');
if (galleryMain) {
  $$('.dg-thumb').forEach(t => {
    t.addEventListener('click', () => {
      $$('.dg-thumb').forEach(x => x.classList.remove('is-active'));
      t.classList.add('is-active');
      // image_tag 由来の srcset が残ると src を替えても表示が変わらない
      galleryMain.removeAttribute('srcset');
      galleryMain.removeAttribute('sizes');
      galleryMain.src = t.dataset.src || t.querySelector('img').src;
    });
  });
}
// qty stepper — the comp styles the <span>, so that stays as the visible value
// and the hidden number input is what the cart form actually posts.
const qtyVal = $('#qtyVal');
if (qtyVal) {
  const qtyInput = $('#Quantity');
  const setQty = next => {
    const value = Math.max(1, next);
    qtyVal.textContent = value;
    if (qtyInput) qtyInput.value = value;
  };
  $('#qtyMinus').addEventListener('click', () => setQty(parseInt(qtyVal.textContent, 10) - 1));
  $('#qtyPlus').addEventListener('click', () => setQty(parseInt(qtyVal.textContent, 10) + 1));
  setQty(parseInt(qtyVal.textContent, 10) || 1);
}
// read more
$$('.readmore-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const box = btn.closest('.clamp-box');
    box.classList.toggle('is-open');
    // 文言は長体用の .ja-c の中だけ差し替える（ボタンごと上書きすると包みが消える）
    (btn.querySelector('.ja-c') || btn).textContent = box.classList.contains('is-open') ? str('close', '閉じる') : str('readMore', '続きを読む');
  });
});
// Editor slider on detail: both cards stay in one horizontal track. Moving the
// track itself keeps the photo, name and copy together without a cross-fade.
const dEditor = $('#detailEditorTrack');
if (dEditor) {
  const counter = $('#detailEditorNow');
  const slides = [...dEditor.children];
  let i = 0;
  const controls = $$('[data-slider="detailEditor"]');
  // Percentage steps with a pixel drag on top, so the swipe can follow the
  // finger without knowing the slide width.
  let dragDx = 0;
  const paint = () => {
    dEditor.style.transform = `translate3d(calc(${-i * 100}% + ${dragDx}px), 0, 0)`;
  };
  const show = next => {
    if (!slides[next] || next === i) return;
    i = next;
    paint();
    slides.forEach((slide, index) => {
      const inactive = index !== i;
      slide.toggleAttribute('inert', inactive);
      slide.setAttribute('aria-hidden', String(inactive));
    });
    if (counter) counter.textContent = i + 1;
    const link = $('.d-editor-foot .paren-btn');
    const href = slides[i].dataset.editorUrl;
    if (link && href) link.href = href;
    controls.forEach(btn => {
      btn.disabled = btn.classList.contains('next')
        ? i === slides.length - 1
        : i === 0;
    });
  };
  slides.forEach((slide, index) => {
    slide.hidden = false;
    const inactive = index !== 0;
    slide.toggleAttribute('inert', inactive);
    slide.setAttribute('aria-hidden', String(inactive));
  });
  controls.forEach(btn => {
    btn.addEventListener('click', () => {
      show(btn.classList.contains('next')
        ? Math.min(i + 1, slides.length - 1)
        : Math.max(i - 1, 0));
    });
  });
  addSwipe(dEditor, {
    pause: () => { dEditor.style.transition = 'none'; },
    onDrag: dx => { dragDx = dx; paint(); },
    onEnd: dir => {
      dragDx = 0;
      dEditor.style.transition = '';
      if (dir) show(Math.max(0, Math.min(i + dir, slides.length - 1)));
      else paint();
    },
  });
  const firstLink = $('.d-editor-foot .paren-btn');
  if (firstLink && slides[0]?.dataset.editorUrl) firstLink.href = slides[0].dataset.editorUrl;
  controls.find(btn => btn.classList.contains('prev'))?.setAttribute('disabled', '');
}

// detail gallery: scrolling over the photo block steps the thumbnails
// (spec calls this "scroll jack"; falls back to clicking, which already works)
const galleryBlock = $('.d-gallery-main');
if (galleryBlock && $$('.dg-thumb').length) {
  const thumbs = $$('.dg-thumb');
  let idx = 0, locked = false;
  galleryBlock.addEventListener('wheel', e => {
    const next = idx + (e.deltaY > 0 ? 1 : -1);
    if (next < 0 || next >= thumbs.length) return;   // let the page scroll on
    e.preventDefault();
    if (locked) return;
    locked = true;
    idx = next;
    thumbs[idx].click();
    setTimeout(() => { locked = false; }, 320);
  }, { passive: false });
  $$('.dg-thumb').forEach((t, k) => t.addEventListener('click', () => { idx = k; }));
}

// クイックビュー（枠内・単体ページ共通）の操作を初期化する。
// root は .qv と .qv-buy を含む要素（埋め込み時はモーダルのホスト、
// 単体ページ表示時は document）。埋め込み時は ctx で閉じ方・カート追加後の
// 振る舞いを親から受け取る。
function mikakuInitQuickView(root, ctx) {
  const embedded = !!(ctx && ctx.embedded);
  const q = sel => root.querySelector(sel);

  // arrows step through this product's own image set, printed on the <img> as
  // an array of ready-made CDN URLs; products with one image get no arrows
  const qvImg = q('#qvProduct');
  if (qvImg) {
    let qvImgs;
    try {
      const list = JSON.parse(qvImg.dataset.qvImages || '[]');
      qvImgs = Array.isArray(list) && list.length ? list : [qvImg.getAttribute('src')];
    } catch (_) {
      qvImgs = [qvImg.getAttribute('src')];
    }
    let qvIdx = 0;
    const qvShow = () => { qvImg.src = qvImgs[qvIdx]; };
    const qvStep = dir => {
      qvIdx = (qvIdx + dir + qvImgs.length) % qvImgs.length;
      qvShow();
    };
    if (qvImgs.length < 2) {
      q('#qvPrev') && (q('#qvPrev').style.display = 'none');
      q('#qvNext') && (q('#qvNext').style.display = 'none');
    }
    qvShow();
    q('#qvPrev') && q('#qvPrev').addEventListener('click', () => qvStep(-1));
    q('#qvNext') && q('#qvNext').addEventListener('click', () => qvStep(1));
  }

  // long-comment clamp toggle
  const qvLong = q('#qvLong');
  const qvMore = q('#qvMore');
  if (qvLong && qvMore) qvMore.addEventListener('click', () => {
    const open = !qvLong.classList.toggle('is-clamped');
    qvMore.classList.toggle('is-open', open);
    qvMore.firstChild.textContent = open ? str('close', '閉じる') : str('readMore', '続きを読む');
  });

  const qvClose = q('#qvClose');
  if (qvClose) qvClose.addEventListener('click', () => {
    if (embedded) ctx.close();
    else location.href = ROUTES.root || '/';
  });

  // 埋め込み時はフォームをそのまま送らず AJAX でカートに入れる
  // （単体ページ表示では従来どおり通常の送信）。
  const qvForm = q('#QuickViewForm');
  if (qvForm && embedded) {
    let qvBuyNow = false;
    qvForm.querySelectorAll('button[type="submit"]').forEach(btn => {
      btn.addEventListener('click', () => { qvBuyNow = btn.hasAttribute('data-buy-now'); });
    });
    qvForm.addEventListener('submit', e => {
      e.preventDefault();
      const data = new FormData(qvForm);
      data.delete('return_to');
      fetch('/cart/add.js', { method: 'POST', body: data })
        .then(r => { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(() => {
          if (qvBuyNow) location.href = THEME.needShipWeek ? (ROUTES.cart || '/cart') : '/checkout';
          else ctx.onCartAdded();
        })
        .catch(() => {
          // 失敗時は商品ページを開く（モーダル内で詰まらせない）
          location.href = ctx.productUrl || '/';
        });
    });
  }
}

// Product cards open the quick-view document as a preloaded modal.
// iframe ではなく、取得した HTML から .qv 本体と購入バーだけを取り出して
// このページの DOM に直接埋め込む。以前は iframe + srcdoc だったが、
// iOS Safari が iframe を中身の寸法まで押し広げる・fixed と zoom の解釈が
// 他エンジンと異なる、という2つの癖で縮尺が壊れ続けたため。同一文書に
// してしまえばページ全体の縮尺(zoom)の中に自然に収まる。
// The markup opts in with `data-detail-modal`, so no URL sniffing is needed.
const detailLinks = $$('a[data-detail-modal]');
// クイックビュー無効時（data-detail-modal が無い）でも、カード全体クリックで
// 商品ページへ飛べるようにする。モーダル有効時は下のブロックが同じ役目を持つ。
if (!detailLinks.length) {
  $$('.product-card, .pick-card, .season-card, .gift-card').forEach(card => {
    const link = $('.pc-name a', card) || $('a.detail-chip[href]', card) || $('a[href*="/products/"]', card);
    if (!link || link.getAttribute('href') === '#') return;
    card.classList.add('is-detail-clickable');
    card.tabIndex = 0;
    card.setAttribute('role', 'link');
    card.setAttribute('aria-label', `${$('.pc-name', card)?.textContent || '商品'}の詳細を見る`);
    card.addEventListener('click', e => {
      if (e.target.closest('a, button, input, select, textarea')) return;
      window.location.href = link.href;
    });
    card.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      window.location.href = link.href;
    });
  });
}
if (detailLinks.length && !document.body.classList.contains('page-detail')) {
  const detailModal = document.createElement('dialog');
  detailModal.className = 'detail-modal';
  detailModal.setAttribute('aria-label', '商品詳細');
  const detailHost = document.createElement('div');
  detailHost.className = 'detail-modal-host';
  detailModal.appendChild(detailHost);
  document.body.appendChild(detailModal);

  let returnFocus = null;
  let closeTimer = 0;
  // Shopify versions its own asset URLs, so the fetch only needs the ?v= stamp
  // when this file is served from the static build.
  const assetVersion = (() => {
    const tag = document.querySelector('script[src*="main.js"]');
    const m = tag && tag.getAttribute('src').match(/[?&]v=([^&]+)/);
    return m ? m[1] : '';
  })();
  const withVersion = href => {
    if (!assetVersion) return href;
    try {
      const u = new URL(href, location.href);
      u.searchParams.set('v', assetVersion);
      return u.href;
    } catch (_) {
      return href;
    }
  };
  const frameCache = new Map();
  const inflight = new Map();
  let requestedUrl = '';
  let renderedUrl = '';
  const setHostReady = ready => detailModal.classList.toggle('is-frame-loading', !ready);
  const fetchDoc = url => {
    const cached = frameCache.get(url);
    if (cached !== undefined) return Promise.resolve(cached);
    const running = inflight.get(url);
    if (running) return running;
    const job = fetch(url, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then(html => {
        frameCache.set(url, html);
        inflight.delete(url);
        return html;
      })
      .catch(err => {
        inflight.delete(url);
        throw err;
      });
    inflight.set(url, job);
    return job;
  };
  // Warm the cache without touching the modal. Hovering a card is enough lead
  // time that the document is usually ready by the time the click lands.
  const prefetch = link => { fetchDoc(withVersion(link.href)).catch(() => { /* noop */ }); };

  const refreshCartCount = () => {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        $$('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(() => {});
  };

  // 460px の板をモーダル幅に合わせて縮める。同一文書なので実測がそのまま
  // 信用でき、適用後にもう一度測って合っていなければ実測比で締め直す。
  const fitEmbed = () => {
    detailHost.style.zoom = '';
    const qv = detailHost.querySelector('.qv');
    if (!qv) return;
    const w = detailHost.clientWidth;
    if (!w || w >= 460) return;
    const z = w / 460;
    detailHost.style.zoom = z;
    const hr = detailHost.getBoundingClientRect();
    const qr = qv.getBoundingClientRect();
    if (qr.width > hr.width + 0.25) detailHost.style.zoom = z * (hr.width / qr.width);
  };
  window.addEventListener('resize', fitEmbed);

  const render = (url, html) => {
    const pdoc = new DOMParser().parseFromString(html, 'text/html');
    const qv = pdoc.querySelector('main.qv');
    const buy = pdoc.querySelector('form.qv-buy');
    if (!qv) {
      // 期待した構造でなければ通常のページ遷移に切り替える
      location.href = url;
      return;
    }
    const nodes = [qv, buy].filter(Boolean).map(n => document.adoptNode(n));
    detailHost.replaceChildren(...nodes);
    renderedUrl = url;
    detailModal.scrollTop = 0;
    mikakuInitQuickView(detailHost, {
      embedded: true,
      close: () => closeDetail(),
      onCartAdded: () => { closeDetail(); refreshCartCount(); },
      productUrl: url.split('?')[0]
    });
    fitEmbed();
    setHostReady(true);
  };
  const loadFrame = url => {
    requestedUrl = url;
    if (renderedUrl === url) {
      detailModal.scrollTop = 0;
      setHostReady(true);
      return;
    }
    const cached = frameCache.get(url);
    if (cached !== undefined) {
      render(url, cached);
      return;
    }
    // Blank the panel straight away; otherwise the product already in the
    // modal stays on screen for the length of the fetch.
    setHostReady(false);
    fetchDoc(url)
      .then(html => { if (requestedUrl === url) render(url, html); })
      .catch(() => {
        requestedUrl = '';
        // Only bail out to the standalone page if the visitor is looking at an
        // empty modal; a failed prefetch must not navigate anyone anywhere.
        if (detailModal.open || detailModal.hasAttribute('open')) location.href = url;
      });
  };
  prefetch(detailLinks[0]);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const afterDetailClose = () => {
    document.body.classList.remove('is-detail-modal-open');
    detailModal.classList.remove('is-visible', 'is-fallback-open');
    returnFocus?.focus({ preventScroll: true });
  };
  const finishDetailClose = () => {
    if (typeof detailModal.close === 'function' && detailModal.open) {
      detailModal.close();
    } else {
      detailModal.removeAttribute('open');
    }
    // close イベント経由でも呼ばれるが、環境によっては close() が
    // イベントを発火しないことがあり、その場合 body の overflow ロックが
    // 残ってページがスクロールできなくなる。直接も呼ぶ（冪等）。
    afterDetailClose();
  };
  const closeDetail = () => {
    if (!detailModal.open && !detailModal.hasAttribute('open')) return;
    clearTimeout(closeTimer);
    detailModal.classList.remove('is-visible');
    closeTimer = window.setTimeout(finishDetailClose, reducedMotion ? 0 : 300);
  };

  const openDetail = link => {
    clearTimeout(closeTimer);
    returnFocus = link;
    loadFrame(withVersion(link.href));
    if (typeof detailModal.showModal === 'function') {
      detailModal.showModal();
    } else {
      detailModal.setAttribute('open', '');
      detailModal.classList.add('is-fallback-open');
    }
    document.body.classList.add('is-detail-modal-open');
    fitEmbed();
    // Two frames ensure the browser paints the slightly inset starting state
    // before transitioning the full-screen detail into place.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      detailModal.classList.add('is-visible');
    }));
  };

  detailLinks.forEach(link => {
    link.addEventListener('click', e => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openDetail(link);
    });
    link.addEventListener('pointerenter', () => prefetch(link));
    link.addEventListener('focus', () => prefetch(link));
  });

  // The reference treats the product as one click target. Make the image,
  // product name, price, editor copy and tags all open the same detail modal.
  $$('.product-card').forEach(card => {
    const link = detailLinks.find(a => card.contains(a));
    if (!link) return;
    card.classList.add('is-detail-clickable');
    card.addEventListener('pointerenter', () => prefetch(link));
    card.tabIndex = 0;
    card.setAttribute('role', 'link');
    card.setAttribute('aria-label', `${$('.pc-name', card)?.textContent || '商品'}の詳細を見る`);
    card.addEventListener('click', e => {
      if (e.target.closest('a, button, input, select, textarea')) return;
      openDetail(link);
    });
    card.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openDetail(link);
    });
  });
  detailModal.addEventListener('cancel', e => {
    e.preventDefault();
    closeDetail();
  });
  // ドロワー表示ではバックドロップ（target = dialog 自身）、全画面表示では
  // 板の左右の余白（target = ホスト）のクリックで閉じる。
  detailModal.addEventListener('click', e => {
    if (e.target === detailModal || e.target === detailHost) closeDetail();
  });
  detailModal.addEventListener('close', afterDetailClose);
}

// Inside the quick-view iframe the page drops its standalone chrome (site
// header, mobile tracking summary) — that is SP-page design, not part of the
// quick view. The RE return artwork stays as the close control.
if (document.body.classList.contains('page-detail') && window.parent !== window) {
  document.body.classList.add('is-quickview');
}

// ---------- quick view page (AI: quick-view02, 460px design) ----------
// 単体ページ（/products/x?view=quick-view を直接開いたとき）のみここを通る。
// モーダル埋め込み時の初期化は mikakuInitQuickView を親側から呼ぶ。
if (document.body.classList.contains('page-quickview')) {
  // the 460px design scales uniformly on narrower viewports
  const fitQv = () => {
    const w = document.documentElement.clientWidth;
    document.body.style.zoom = w < 460 ? (Math.floor(w) - 1) / 460 : '';
  };
  window.addEventListener('resize', fitQv);
  fitQv();
  mikakuInitQuickView(document, { embedded: false });
}

// In modal use the RE control closes the overlay; direct visits still return
// to the product list through the link's normal href.
const detailReturn = $('.page-detail .return-btn');
if (detailReturn && window.parent !== window) {
  detailReturn.addEventListener('click', e => {
    e.preventDefault();
    window.parent.postMessage({ type: 'mikaku:close-detail' }, '*');
  });
}

/* ============================================================
   Shopify form wiring
   ============================================================ */

// 今すぐ注文 adds the line and continues straight to checkout; カートに入れる
// returns to the page it was pressed on. Both go through the same product form,
// so the only difference is the `return_to` value carried with the POST.
$$('form[action$="/cart/add"], form[action*="/cart/add"]').forEach(form => {
  const returnTo = $('[data-return-to]', form);
  if (!returnTo) return;
  $$('button[type="submit"]', form).forEach(button => {
    button.addEventListener('click', () => {
      // 発送希望週が未選択なら、いったんカートへ寄って選んでもらう
      const buyNowTo = THEME.needShipWeek ? (ROUTES.cart || '/cart') : '/checkout';
      returnTo.value = button.hasAttribute('data-buy-now') ? buyNowTo : 'back';
    });
  });
});

// Variant picker: keep the posted id, the displayed price and the URL in step.
// バリエーションのピルボタン: 選択で hidden の variant id と価格表示を更新
$$('.dl-variants [data-variant-btn]').forEach(btn => {
  const form = btn.closest('form');
  const idInput = form && $('[data-variant-id]', form);
  btn.addEventListener('click', () => {
    $$('.dl-variants [data-variant-btn]').forEach(b => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-pressed', String(b === btn));
    });
    if (idInput) idInput.value = btn.value;
    // 価格横の内容量（バリエーションごとの weight_label）を先に差し替えてから
    // 価格を組み直す
    const w = btn.dataset.weight || '';
    $$('.dl-weight, .mobile-buy-price b').forEach(el => {
      el.textContent = w;
      el.hidden = !w;
    });
    $$('.dl-slash').forEach(el => { el.hidden = !w; });
    const price = btn.dataset.price;
    if (price) {
      $$('.dl-price, .mobile-buy-price span').forEach(el => {
        const weight = $('.dl-weight', el);
        el.innerHTML = weight ? weight.outerHTML + $('.dl-slash', el).outerHTML + price : price;
      });
    }
    // バリアントに画像が紐づいていれば detail-left のメイン写真を差し替える
    // （dl-visual 先頭の img は旬バッジなので badge-* は除外する）
    const image = btn.dataset.image;
    const main = $('.detail-left .dl-visual img:not([class*="badge-"])');
    if (image && main) {
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.src = image;
    }
    const url = new URL(location.href);
    url.searchParams.set('variant', btn.value);
    history.replaceState({}, '', url);
  });
});

$$('[data-variant-select]').forEach(select => {
  const form = select.closest('form');
  const idInput = form && $('[data-variant-id]', form);
  select.addEventListener('change', () => {
    const option = select.selectedOptions[0];
    if (idInput) idInput.value = select.value;
    const price = option && option.dataset.price;
    if (price) {
      $$('.dl-price, .mobile-buy-price span').forEach(el => {
        const weight = $('.dl-weight', el);
        el.innerHTML = weight ? weight.outerHTML + $('.dl-slash', el).outerHTML + price : price;
      });
    }
    const url = new URL(location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState({}, '', url);
  });
});

// Storefront filters render as links; the comp draws the boolean ones as
// switches, so the checkbox just follows its link.
$$('input[data-facet-url]').forEach(input => {
  input.addEventListener('change', () => { location.href = input.dataset.facetUrl; });
});

// The sort control is a styled <select> inside a GET form.
const sortBy = $('#SortBy');
if (sortBy) sortBy.addEventListener('change', () => sortBy.form.submit());

// Chrome and Firefox match :focus-visible on a <select> for pointer input too,
// not just keyboard, so the sort pill drew a focus ring on every tap. Record
// the modality here and let the stylesheet ring only under [data-kbd].
addEventListener('keydown', e => {
  if (e.key === 'Tab') document.documentElement.dataset.kbd = '';
}, true);
addEventListener('pointerdown', () => {
  delete document.documentElement.dataset.kbd;
}, true);

// 予約販売のお知らせ: 商品ページを開いたら出す。「1回だけ」設定のときは
// 同じ訪問(sessionStorage)で出し直さない。本文を書き換えたら再び出る。
const preorderPopup = $('[data-preorder-popup]');
if (preorderPopup) {
  const SEEN_KEY = 'mikaku:preorder-notice';
  const rev = preorderPopup.dataset.rev || '1';
  const once = preorderPopup.dataset.once === 'true';
  let seen = false;
  try { seen = once && sessionStorage.getItem(SEEN_KEY) === rev; } catch (_) { /* プライベートモード */ }
  // 最終日を過ぎたら開かない（ページがキャッシュから来た場合の保険）
  const until = preorderPopup.dataset.until;
  if (until && !Number.isNaN(Date.parse(`${until}T23:59:59`)) && new Date(`${until}T23:59:59`) < new Date()) seen = true;
  const closePreorder = () => { if (preorderPopup.open) preorderPopup.close(); };
  $$('[data-preorder-close]', preorderPopup).forEach(button => button.addEventListener('click', closePreorder));
  // 背景（バックドロップ）のクリックでも閉じる
  preorderPopup.addEventListener('click', event => { if (event.target === preorderPopup) closePreorder(); });
  if (!seen) {
    // ページの縮尺(zoom)が決まってから開く
    window.setTimeout(() => {
      if (typeof preorderPopup.showModal === 'function') preorderPopup.showModal();
      else preorderPopup.setAttribute('open', '');
      try { if (once) sessionStorage.setItem(SEEN_KEY, rev); } catch (_) { /* 保存できなくても表示は済んでいる */ }
    }, 350);
  }
}

// 発送希望週: 選んだ時点でカート属性に保存する（どのボタンで進んでも残る）。
// また、ページがキャッシュから来て選択肢が古い場合に備え、クライアント時刻で
// 「翌週の月曜」より前の週を取り除く（時差ぶん2日の余裕を見る）。
const shipWeek = $('[data-ship-week]');
if (shipWeek) {
  const mondayOf = date => {
    const m = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
    return m;
  };
  const cutoff = mondayOf(new Date());
  cutoff.setDate(cutoff.getDate() + 5);
  [...shipWeek.options].forEach(option => {
    if (!option.dataset.mon) return;
    const [y, m, d] = option.dataset.mon.split('-').map(Number);
    if (new Date(y, m - 1, d) < cutoff) option.remove();
  });
  shipWeek.addEventListener('change', () => {
    fetch(ROUTES.cartUpdate || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { '発送希望週': shipWeek.value } }),
    }).catch(() => { /* フォーム送信時にも同じ値が送られるので無視 */ });
  });
}

// Cart quantity fields submit the surrounding update form on change.
$$('[data-cart-quantity]').forEach(input => {
  input.addEventListener('change', () => {
    const form = input.closest('form');
    if (!form) return;
    const update = document.createElement('input');
    update.type = 'hidden';
    update.name = 'update';
    update.value = '';
    form.appendChild(update);
    form.submit();
  });
});

// Login page: reveal the password-recovery form without a round trip.
const recoverLink = $('#RecoverPasswordLink');
const recoverForm = $('#RecoverPasswordForm');
if (recoverLink && recoverForm) {
  recoverLink.addEventListener('click', event => {
    event.preventDefault();
    recoverForm.hidden = !recoverForm.hidden;
    if (!recoverForm.hidden) $('#RecoverEmail', recoverForm).focus();
  });
  // Shopify redirects back with ?form_type=recover_customer_password on submit.
  if (location.search.indexOf('recover') !== -1 || location.hash === '#recover') {
    recoverForm.hidden = false;
  }
}

// ---------- アンカーエイリアス ----------
// リンク側の分かりやすいハッシュ(#field-log 等)を実セクションIDへ解決する。
// body.zoom で読み込み後にレイアウト高さが変わるため、load 後に再スクロール
// して着地位置も安定させる。
(() => {
  const HASH_ALIASES = {
    'field-log': 'fieldlog',
    'ingredient-dictionary': 'dictionary',
    'editor': 'abouteditor',
  };
  const jumpToHash = () => {
    const raw = decodeURIComponent(location.hash.slice(1));
    if (!raw) return;
    const el = document.getElementById(raw) || document.getElementById(HASH_ALIASES[raw] || '');
    if (el) el.scrollIntoView();
  };
  window.addEventListener('hashchange', jumpToHash);
  window.addEventListener('load', () => window.setTimeout(jumpToHash, 150));
})();
